import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./SkillVisualization.css";

export default function SkillVisualization() {
  const [skills, setSkills] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [chatQuestion, setChatQuestion] = useState("");
  const [chatResponse, setChatResponse] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchSkills();
  }, []);

  useEffect(() => {
    let animationId;
    if (skills && canvasRef.current) {
      const animate = () => {
        drawSkillConstellation();
        animationId = requestAnimationFrame(animate);
      };
      animate();
    }
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skills, selectedCategory, hoveredSkill]);

  const fetchSkills = async () => {
    try {
      const response = await fetch("/api/analyze-skills");
      const data = await response.json();
      if (data.ok) {
        setSkills(data.skills);
      }
    } catch (error) {
      console.error("Failed to fetch skills:", error);
    }
  };

  const analyzeSkills = async (targetRole = "Full-Stack Engineer") => {
    try {
      setLoading(true);
      const response = await fetch("/api/analyze-skills", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetRole }),
      });
      const data = await response.json();
      if (data.ok) {
        setAnalysis(data.analysis);
      }
    } catch (error) {
      console.error("Failed to analyze skills:", error);
    } finally {
      setLoading(false);
    }
  };

  const askAI = async () => {
    if (!chatQuestion.trim()) return;
    
    try {
      setChatLoading(true);
      setChatResponse("");
      const response = await fetch("/api/analyze-skills", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: chatQuestion }),
      });
      const data = await response.json();
      if (data.ok) {
        setChatResponse(data.analysis.response || JSON.stringify(data.analysis, null, 2));
      }
    } catch (error) {
      setChatResponse("Sorry, I couldn't process that question. Please try again.");
    } finally {
      setChatLoading(false);
    }
  };

  const drawSkillConstellation = () => {
    const canvas = canvasRef.current;
    if (!canvas || !skills) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width = canvas.offsetWidth * 2; // Retina
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Get skills to display
    let allSkills = [];
    if (selectedCategory === "all") {
      Object.values(skills).forEach(categorySkills => {
        allSkills = [...allSkills, ...categorySkills];
      });
    } else {
      allSkills = skills[selectedCategory] || [];
    }

    if (allSkills.length === 0) return;

    // Create constellation points
    const centerX = width / 4;
    const centerY = height / 4;
    const radius = Math.min(width, height) / 5;

    // Animate time-based rotation
    const time = Date.now() * 0.0001;

    allSkills.forEach((skill, index) => {
      const angle = (index / allSkills.length) * Math.PI * 2 + time;
      const distance = radius * (0.6 + skill.level / 200);
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance;

      // Draw connections to nearby skills
      allSkills.forEach((otherSkill, otherIndex) => {
        if (otherIndex <= index) return;
        const otherAngle = (otherIndex / allSkills.length) * Math.PI * 2 + time;
        const otherDistance = radius * (0.6 + otherSkill.level / 200);
        const otherX = centerX + Math.cos(otherAngle) * otherDistance;
        const otherY = centerY + Math.sin(otherAngle) * otherDistance;

        const dist = Math.hypot(x - otherX, y - otherY);
        if (dist < radius * 0.8) {
          // Get colors for gradient connection
          const color1 = getCategoryColor(skill.category);
          const color2 = getCategoryColor(otherSkill.category);
          
          // Create gradient for connection line
          const gradient = ctx.createLinearGradient(x, y, otherX, otherY);
          gradient.addColorStop(0, color1 + '40'); // 40 is alpha in hex
          gradient.addColorStop(1, color2 + '40');
          
          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1.5;
          ctx.moveTo(x, y);
          ctx.lineTo(otherX, otherY);
          ctx.stroke();
        }
      });

      // Draw skill node
      const isHovered = hoveredSkill === skill.name;
      const nodeRadius = isHovered ? 10 : 5 + skill.level / 20;
      const skillColor = getCategoryColor(skill.category);

      // Glow effect
      if (isHovered) {
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, nodeRadius * 3);
        gradient.addColorStop(0, skillColor + '80'); // 80 is alpha
        gradient.addColorStop(1, skillColor + '00');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, nodeRadius * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Node circle with gradient
      const nodeGradient = ctx.createRadialGradient(x, y, 0, x, y, nodeRadius);
      nodeGradient.addColorStop(0, skillColor);
      nodeGradient.addColorStop(1, skillColor + 'CC'); // Slightly transparent edge
      
      ctx.beginPath();
      ctx.fillStyle = isHovered ? skillColor : nodeGradient;
      ctx.arc(x, y, nodeRadius, 0, Math.PI * 2);
      ctx.fill();
      
      // Add white border for better visibility
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.stroke();

      // Skill label
      if (isHovered || skill.level > 80) {
        ctx.font = isHovered ? "bold 14px Arial" : "bold 12px Arial";
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        
        // Add shadow for better readability
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        
        ctx.fillText(skill.name, x, y - nodeRadius - 8);
        
        // Reset shadow
        ctx.shadowBlur = 0;
        
        // Level indicator with category color
        if (isHovered) {
          ctx.font = "bold 11px Arial";
          ctx.fillStyle = skillColor;
          ctx.fillText(`${skill.level}%`, x, y - nodeRadius - 24);
        }
      }
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      languages: "💻",
      frontend: "🎨",
      backend: "⚙️",
      devops: "🚀",
      cloud: "☁️",
      database: "🗄️",
      tools: "🔧",
      Backend: "⚙️",
      Frontend: "🎨",
      DevOps: "🚀",
      Cloud: "☁️",
      Database: "🗄️",
      Tools: "🔧",
      Systems: "🖥️",
    };
    return icons[category] || "⭐";
  };

  const getCategoryColor = (category) => {
    const colors = {
      languages: "#FF6B6B",      // Vibrant Red
      frontend: "#4ECDC4",       // Turquoise
      backend: "#45B7D1",        // Sky Blue
      devops: "#96CEB4",         // Mint Green
      cloud: "#FFEAA7",          // Soft Yellow
      database: "#DFE6E9",       // Light Gray
      tools: "#A29BFE",          // Lavender
      Backend: "#45B7D1",        // Capital B
      Frontend: "#4ECDC4",       // Capital F
      DevOps: "#96CEB4",         // Capital D
      Cloud: "#FFEAA7",          // Capital C
      Database: "#DFE6E9",       // Capital D
      Tools: "#A29BFE",          // Capital T
      Systems: "#FF6B6B",        // For C language
    };
    return colors[category] || "#c770f0";
  };

  if (!skills) {
    return (
      <div className="skill-viz-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading skills...</p>
        </div>
      </div>
    );
  }

  const allSkillsFlat = Object.values(skills).flat();
  const avgLevel = Math.round(
    allSkillsFlat.reduce((sum, s) => sum + s.level, 0) / allSkillsFlat.length
  );

  return (
    <div className="skill-viz-container">
      {/* Header */}
      <motion.div
        className="skill-viz-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1>🎨 AI Skill Visualization</h1>
          <p>Interactive 3D skill analysis powered by AI</p>
        </div>
        <div className="skill-stats">
          <div className="stat-item">
            <span className="stat-value">{allSkillsFlat.length}</span>
            <span className="stat-label">Skills</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{avgLevel}%</span>
            <span className="stat-label">Avg Level</span>
          </div>
        </div>
      </motion.div>

      {/* Category Filter */}
      <div className="category-filter">
        <button
          className={`category-btn ${selectedCategory === "all" ? "active" : ""}`}
          onClick={() => setSelectedCategory("all")}
        >
          🌟 All Skills
        </button>
        {Object.keys(skills).map((category) => (
          <button
            key={category}
            className={`category-btn ${selectedCategory === category ? "active" : ""}`}
            onClick={() => setSelectedCategory(category)}
            style={{ 
              borderColor: getCategoryColor(category),
              background: selectedCategory === category 
                ? getCategoryColor(category) + '40'
                : 'rgba(199, 112, 240, 0.1)'
            }}
          >
            {getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      <div className="skill-viz-content">
        {/* 3D Constellation Canvas */}
        <div className="constellation-container">
          <canvas
            ref={canvasRef}
            className="skill-canvas"
            onMouseMove={(e) => {
              // Detect hover (for future enhancement)
              const rect = e.target.getBoundingClientRect();
              const mouseX = e.clientX - rect.left;
              const mouseY = e.clientY - rect.top;
              // TODO: Calculate nearest skill node based on mouseX, mouseY
              console.log("Mouse position:", mouseX, mouseY);
            }}
          />
          <div className="canvas-overlay">
            <p>💡 Hover over nodes to see details • Size represents proficiency</p>
          </div>
        </div>

        {/* Skill Grid */}
        <div className="skills-grid">
          {(selectedCategory === "all" ? allSkillsFlat : skills[selectedCategory] || []).map(
            (skill, idx) => (
              <motion.div
                key={idx}
                className="skill-card"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
                onMouseEnter={() => setHoveredSkill(skill.name)}
                onMouseLeave={() => setHoveredSkill(null)}
                style={{ 
                  borderColor: getCategoryColor(skill.category.toLowerCase()),
                  boxShadow: hoveredSkill === skill.name 
                    ? `0 12px 30px ${getCategoryColor(skill.category.toLowerCase())}60`
                    : 'none'
                }}
              >
                <div className="skill-card-header">
                  <span className="skill-name">
                    {getCategoryIcon(skill.category)} {skill.name}
                  </span>
                  <span className="skill-level">{skill.level}%</span>
                </div>
                <div className="skill-progress-bar">
                  <motion.div
                    className="skill-progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.level}%` }}
                    transition={{ duration: 0.8, delay: idx * 0.05 }}
                    style={{ background: getCategoryColor(skill.category.toLowerCase()) }}
                  />
                </div>
                <span className="skill-category">{skill.category}</span>
              </motion.div>
            )
          )}
        </div>
      </div>

      {/* AI Analysis Section */}
      <div className="ai-analysis-section">
        <h2>🤖 AI-Powered Analysis</h2>
        <div className="analysis-controls">
          <select
            className="role-selector"
            onChange={(e) => analyzeSkills(e.target.value)}
            defaultValue=""
          >
            <option value="" disabled>
              Select target role...
            </option>
            <option value="Full-Stack Engineer">Full-Stack Engineer</option>
            <option value="DevOps Engineer">DevOps Engineer</option>
            <option value="Backend Developer">Backend Developer</option>
            <option value="Cloud Architect">Cloud Architect</option>
            <option value="Site Reliability Engineer">SRE</option>
          </select>
          <button className="analyze-btn" onClick={() => analyzeSkills()} disabled={loading}>
            {loading ? "🤖 Analyzing..." : "🔍 Analyze Skills"}
          </button>
        </div>

        <AnimatePresence>
          {analysis && (
            <motion.div
              className="analysis-results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {analysis.overallScore && (
                <div className="overall-score">
                  <div className="score-circle">
                    <span className="score-value">{analysis.overallScore}</span>
                    <span className="score-max">/100</span>
                  </div>
                  <p className="score-label">Overall Skill Score</p>
                </div>
              )}

              {analysis.summary && (
                <div className="analysis-card summary-card">
                  <h3>📋 Summary</h3>
                  <p>{analysis.summary}</p>
                </div>
              )}

              {analysis.strengths && (
                <div className="analysis-card">
                  <h3>💪 Strengths</h3>
                  <ul>
                    {analysis.strengths.map((strength, idx) => (
                      <li key={idx}>✅ {strength}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.recommendations && (
                <div className="analysis-card">
                  <h3>🎯 Recommendations</h3>
                  <ul>
                    {analysis.recommendations.map((rec, idx) => (
                      <li key={idx}>💡 {rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.careerFit && (
                <div className="analysis-card">
                  <h3>🎯 Career Fit</h3>
                  <div className="career-fit-bars">
                    {Object.entries(analysis.careerFit).map(([role, score]) => (
                      <div key={role} className="fit-bar-container">
                        <span className="fit-role">
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </span>
                        <div className="fit-bar">
                          <div className="fit-bar-fill" style={{ width: `${score}%` }}></div>
                        </div>
                        <span className="fit-score">{score}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.marketValue && (
                <div className="analysis-card market-card">
                  <h3>💰 Market Value</h3>
                  <p className="market-level">{analysis.marketValue.toUpperCase()}</p>
                  {analysis.estimatedSalary && (
                    <p className="salary-range">{analysis.estimatedSalary}</p>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ask AI Chat */}
      <button className="chat-toggle-btn" onClick={() => setShowChat(!showChat)}>
        💬 Ask AI
      </button>

      <AnimatePresence>
        {showChat && (
          <motion.div
            className="ai-chat-box"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
          >
            <div className="chat-header">
              <h3>🤖 Ask About My Skills</h3>
              <button className="chat-close" onClick={() => setShowChat(false)}>
                ✕
              </button>
            </div>
            <div className="chat-content">
              {chatResponse && (
                <div className="chat-response">
                  <p>{chatResponse}</p>
                </div>
              )}
              <div className="chat-input-container">
                <input
                  type="text"
                  className="chat-input"
                  placeholder="e.g., What's your strongest area?"
                  value={chatQuestion}
                  onChange={(e) => setChatQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && askAI()}
                />
                <button
                  className="chat-send-btn"
                  onClick={askAI}
                  disabled={chatLoading || !chatQuestion.trim()}
                >
                  {chatLoading ? "..." : "Send"}
                </button>
              </div>
              <div className="chat-suggestions">
                <p>Try asking:</p>
                <button onClick={() => setChatQuestion("What skills should I improve?")}>
                  What skills should I improve?
                </button>
                <button onClick={() => setChatQuestion("Am I ready for a senior role?")}>
                  Am I ready for a senior role?
                </button>
                <button onClick={() => setChatQuestion("What's my unique strength?")}>
                  What's my unique strength?
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
